import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Mail, Lock, ArrowLeft } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { IMAGES } from "../../data";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Invalid email or password.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls =
  "w-full bg-night border border-cream/15 text-cream placeholder:text-cream/30 px-5 py-4 pl-12 text-sm focus:outline-none focus:border-amber transition-colors duration-300";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/hub" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      navigate("/hub");
    } catch (err) {
      setError(err.response ? formatApiErrorDetail(err.response?.data?.detail) : "Cannot reach the server — please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-forest grid grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={IMAGES.chaletsDusk} alt="LUSH Adventure estate at dusk" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-night/95 via-night/40 to-night/30" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <span className="inline-block bg-cream px-3 py-2 rounded-sm w-fit">
            <img src="/logo.png" alt="LUSH Adventure @ Barn" className="h-11 w-auto" />
          </span>
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-amber mb-4">Operations Hub</p>
            <h1 className="font-display text-5xl text-cream leading-[1.05] tracking-tight">
              Behind every <span className="italic text-amber">legendary</span> day out.
            </h1>
            <p className="mt-5 text-cream/70 max-w-md text-base leading-relaxed">
              Events, bookings, zones, packages and invoicing — the command center for Admins and Relationship Managers.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md"
        >
          <span className="inline-block lg:hidden bg-cream px-3 py-2 rounded-sm mb-10">
            <img src="/logo.png" alt="LUSH Adventure @ Barn" className="h-10 w-auto" />
          </span>
          <p className="text-xs tracking-[0.3em] uppercase text-amber mb-4">Staff Access</p>
          <h2 className="font-display text-4xl text-cream tracking-tight mb-2">Sign in to the Hub</h2>
          <p className="text-sm text-cream/60 mb-10">Admin and Relationship Manager accounts only.</p>

          <form onSubmit={submit} data-testid="login-form" className="space-y-5">
            <div className="relative">
              <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-cream/40" />
              <input
                data-testid="login-email-input"
                type="email"
                required
                placeholder="Email address"
                className={inputCls}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="relative">
              <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-cream/40" />
              <input
                data-testid="login-password-input"
                type="password"
                required
                placeholder="Password"
                className={inputCls}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/10 px-4 py-3">
                {error}
              </p>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="flex items-center justify-center gap-3 w-full bg-amber text-night text-xs tracking-[0.25em] uppercase font-semibold px-8 py-5 hover:bg-cream transition-colors duration-300 disabled:opacity-60"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <Link
            to="/"
            data-testid="login-back-home"
            className="mt-10 inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-cream/50 hover:text-amber transition-colors duration-300"
          >
            <ArrowLeft size={14} /> Back to website
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
